<template>
  <Teleport to="body">
    <div
      v-if="open"
      :class="overlayClass"
      @keydown.esc="cancelDialog"
      @keydown.tab.prevent="cycleDialogFocus"
    >
      <section
        ref="dialogRef"
        aria-modal="true"
        :class="dialogClass"
        role="alertdialog"
        tabindex="-1"
        :aria-describedby="descriptionId"
        :aria-labelledby="titleId"
      >
        <header :class="headerClass" :id="titleId">{{ title }}</header>
        <div :class="bodyClass" :id="descriptionId">
          <p>{{ message }}</p>
          <p v-if="consequence" :class="consequenceClass">{{ consequence }}</p>
          <label v-if="requireTypedText" :class="fieldClass">
            <span>Type {{ requireTypedText }} to confirm</span>
            <input ref="inputRef" v-model="typedText" :class="inputClass" autocomplete="off" />
          </label>
        </div>
        <footer :class="footerClass">
          <button ref="cancelRef" :class="cancelButtonClass" type="button" @click="cancelDialog">
            {{ cancelLabel }}
          </button>
          <button
            :class="resolvedConfirmButtonClass"
            type="button"
            :disabled="confirmDisabled"
            @click="confirmDialog"
          >
            {{ confirmLabel }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    bodyClass?: string;
    cancelButtonClass?: string;
    cancelLabel?: string;
    confirmButtonClass?: string;
    confirmLabel?: string;
    consequence?: string | undefined;
    consequenceClass?: string;
    dangerConfirmButtonClass?: string;
    dialogClass?: string;
    fieldClass?: string;
    financialConfirmButtonClass?: string;
    footerClass?: string;
    headerClass?: string;
    inputClass?: string;
    message: string;
    open: boolean;
    overlayClass?: string;
    requireTypedText?: string | undefined;
    title: string;
    tone?: "default" | "danger" | "financial";
  }>(),
  {
    bodyClass: "price-rescue-confirmation-dialog__body",
    cancelButtonClass: "price-rescue-confirmation-dialog__button",
    cancelLabel: "Cancel",
    confirmButtonClass:
      "price-rescue-confirmation-dialog__button price-rescue-confirmation-dialog__button--primary",
    confirmLabel: "Confirm",
    consequenceClass: "price-rescue-confirmation-dialog__consequence",
    dangerConfirmButtonClass:
      "price-rescue-confirmation-dialog__button price-rescue-confirmation-dialog__button--danger",
    dialogClass: "price-rescue-confirmation-dialog",
    fieldClass: "price-rescue-confirmation-dialog__field",
    financialConfirmButtonClass:
      "price-rescue-confirmation-dialog__button price-rescue-confirmation-dialog__button--primary",
    footerClass: "price-rescue-confirmation-dialog__footer",
    headerClass: "price-rescue-confirmation-dialog__header",
    inputClass: "price-rescue-confirmation-dialog__input",
    overlayClass: "price-rescue-confirmation-dialog__overlay",
    tone: "default",
  },
);

const emit = defineEmits<{
  cancel: [];
  confirm: [];
}>();

const typedText = ref("");
const cancelRef = ref<HTMLButtonElement>();
const dialogRef = ref<HTMLElement>();
const inputRef = ref<HTMLInputElement>();
const titleId = "price-rescue-confirmation-title";
const descriptionId = "price-rescue-confirmation-description";
let returnFocusTarget: HTMLElement | null = null;

const confirmDisabled = computed(() => {
  return props.requireTypedText !== undefined && typedText.value !== props.requireTypedText;
});

const resolvedConfirmButtonClass = computed(() => {
  if (props.tone === "danger") return props.dangerConfirmButtonClass;
  if (props.tone === "financial") return props.financialConfirmButtonClass;
  return props.confirmButtonClass;
});

watch(() => props.open, handleOpenChange);
onMounted(() => {
  if (props.open) void handleOpenChange(true);
});

async function handleOpenChange(open: boolean): Promise<void> {
  if (!open) {
    restoreFocusToOpener();
    return;
  }

  rememberFocusTarget();
  typedText.value = "";
  await nextTick();
  focusFirstInteractiveElement();
}

function cancelDialog(): void {
  emit("cancel");
}

function confirmDialog(): void {
  if (confirmDisabled.value) return;
  emit("confirm");
}

function cycleDialogFocus(event: KeyboardEvent): void {
  const focusableElements = dialogFocusableElements();
  if (focusableElements.length === 0) {
    dialogRef.value?.focus();
    return;
  }

  const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
  const nextIndex = nextFocusableIndex(event.shiftKey, currentIndex, focusableElements.length);
  focusableElements[nextIndex]?.focus();
}

function focusFirstInteractiveElement(): void {
  if (inputRef.value !== undefined) {
    inputRef.value.focus();
    return;
  }

  cancelRef.value?.focus();
  dialogRef.value?.focus();
}

function rememberFocusTarget(): void {
  returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

function restoreFocusToOpener(): void {
  returnFocusTarget?.focus();
  returnFocusTarget = null;
}

function dialogFocusableElements(): HTMLElement[] {
  return Array.from(
    dialogRef.value?.querySelectorAll<HTMLElement>(focusableSelector()) ?? [],
  ).filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);
}

function focusableSelector(): string {
  return "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";
}

function nextFocusableIndex(
  isShiftTab: boolean,
  currentIndex: number,
  focusableCount: number,
): number {
  if (currentIndex === -1) return isShiftTab ? focusableCount - 1 : 0;
  if (isShiftTab) return currentIndex === 0 ? focusableCount - 1 : currentIndex - 1;
  return currentIndex === focusableCount - 1 ? 0 : currentIndex + 1;
}
</script>
